import { useQuery } from '@tanstack/react-query';
import { DocumentSourceService } from 'src/services/DocumentSourceService';
import { parseTemplateTokenPanelData } from 'src/utils/documentSourceUtils';

type DocumentSourceResponse = {
    metadata: {
        documentSources: {
            panels: Array<{
                source: string;
                data: string | { templateToken?: string } | { dynamicLineId: string; colorVariantId: string };
            }>;
        }
    },
    document?: Record<string, unknown>;
};

type TemplateIdentifier = {
    type: 'TEMPLATE_TOKEN' | 'DYNAMIC_DESIGN';
    value: string | null;
}

const buildTemplateIdentifier = (documentSourceQuery: DocumentSourceResponse): TemplateIdentifier | null => {
    const firstIdentifierPanel = documentSourceQuery?.metadata?.documentSources?.panels?.find((panel) => panel.source === 'TEMPLATE_TOKEN' || panel.source === 'DYNAMIC_DESIGN');

    // Check for template token from a static design
    if (firstIdentifierPanel?.source === 'TEMPLATE_TOKEN') {
        return {
            type: 'TEMPLATE_TOKEN',
            value: parseTemplateTokenPanelData(firstIdentifierPanel.data),
        };
    }

    // Check for dynamic design
    if (firstIdentifierPanel?.source === 'DYNAMIC_DESIGN' && typeof firstIdentifierPanel.data === 'object' && firstIdentifierPanel.data !== null) {
        const { dynamicLineId, colorVariantId } = firstIdentifierPanel.data as { dynamicLineId: string; colorVariantId: string };

        return {
            type: 'DYNAMIC_DESIGN',
            value: `${dynamicLineId}_d_${colorVariantId}`,
        };
    }

    return null;
};

export interface DocumentSourceQuery {
    isLoading: boolean;
    error: Error | null;
    data: (DocumentSourceResponse & { templateIdentifier: TemplateIdentifier | null }) | undefined;
}

export const useDocumentSource = (documentUri: string): DocumentSourceQuery => {
    const query = useQuery({
        queryKey: ['documentSource', documentUri],
        queryFn: async (): Promise<DocumentSourceResponse & { templateIdentifier: TemplateIdentifier | null }> => {
            if (!documentUri) {
                throw new Error('Missing document URI');
            }

            const result = await DocumentSourceService.fetchDocumentSource(documentUri);
            const templateIdentifier = buildTemplateIdentifier(result);
            const templateIdentifierValue = templateIdentifier?.value || null;

            // Validate template identifier but don't throw - let components handle validation as needed
            if (templateIdentifier && !templateIdentifierValue) {
                throw new Error(`Document with template identifier of type '${templateIdentifier.type}' has an invalid value '${templateIdentifier.value}'`);
            }

            return { ...result, templateIdentifier };
        },
        enabled: !!documentUri && documentUri.startsWith('http'),
        refetchOnWindowFocus: false,
        suspense: false,
        retry: false,
    });

    return {
        isLoading: query.isLoading,
        error: query.error as Error | null,
        data: query.data,
    };
};
